import { useCallback, useMemo, useState } from 'react';

import { useI18nTranslation } from 'platform-i18n-utils';

import { validateFields } from '../components/utils';
import { FIELD_TYPES } from '../constants';
import { buildTicketSubmitPayload } from '../utils';

const getInitialFieldValue = field => {
  // readOnly fields (e.g. assignTo, priority) are never rendered, but their
  // value must still be included in the submit payload.
  if (field.readOnly) {
    return field.value ?? '';
  }

  return field.type === FIELD_TYPES.FILE ? [] : '';
};

const getInitialValues = fields =>
  fields.reduce((values, field) => {
    values[field.id] = getInitialFieldValue(field);

    return values;
  }, {});

export const useTicketFormState = config => {
  const { t } = useI18nTranslation();
  const fields = config?.fields ?? [];
  const [values, setValues] = useState(() => getInitialValues(fields));
  // Tracks which fields the user has left (blurred) at least once - a field's
  // error is hidden until then, so a pristine form never shows errors on load.
  const [touched, setTouched] = useState({});
  // Once a submit is blocked, every field's error becomes visible (even
  // fields the user hasn't focused yet) so nothing is silently hidden.
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const setFieldValue = useCallback((fieldId, value) => {
    setValues(previousValues => ({ ...previousValues, [fieldId]: value }));
  }, []);

  const touchField = useCallback(fieldId => {
    setTouched(previousTouched =>
      previousTouched[fieldId] ? previousTouched : { ...previousTouched, [fieldId]: true }
    );
  }, []);

  // Recomputed on every value change so which fields are invalid stays current.
  const errors = useMemo(() => validateFields(fields, values, t), [fields, values, t]);
  const isValid = useMemo(() => Object.keys(errors).length === 0, [errors]);

  // What's actually shown in the UI: only errors for fields that have been
  // touched (or every error, once a submit attempt has been blocked).
  const visibleErrors = useMemo(() => {
    if (submitAttempted) {
      return errors;
    }

    return Object.keys(errors).reduce((visible, fieldId) => {
      if (touched[fieldId]) {
        visible[fieldId] = errors[fieldId];
      }

      return visible;
    }, {});
  }, [errors, touched, submitAttempted]);

  const prepareSubmit = useCallback(() => {
    if (!isValid) {
      setSubmitAttempted(true);

      return null;
    }

    return buildTicketSubmitPayload(config, values);
  }, [isValid, config, values]);

  return {
    values,
    setFieldValue,
    touchField,
    prepareSubmit,
    errors: visibleErrors,
    isValid
  };
};
